import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUpRight, type LucideIcon, Wrench } from "lucide-react";
import { JsonLd } from "@/components/JsonLd";
import { breadcrumbListSchema } from "@/lib/schema";
import {
	buildLocalizedSeoLinks,
	getLocalizedSeoUrls,
	ogImageUrl,
} from "@/lib/seo";
import { docsSections } from "@/routes/docs";

export const Route = createFileRoute("/docs/")({
	component: DocsIndexPage,
	head: ({ matches }) => {
		const seoUrls = getLocalizedSeoUrls({ matches, internalPath: "/docs" });
		const title = "AgentMRR Docs - List & Verify AI Agents";
		const description =
			"Index of AgentMRR docs. How to submit an agent to the leaderboard with a single REST call.";
		return {
			meta: [
				{ title },
				{ name: "description", content: description },
				{ property: "og:title", content: title },
				{ property: "og:description", content: description },
				{ property: "og:image", content: ogImageUrl("docs") },
				{ property: "og:url", content: seoUrls.canonicalUrl },
				{ property: "og:type", content: "website" },
				{ name: "twitter:card", content: "summary_large_image" },
				{ name: "twitter:image", content: ogImageUrl("docs") },
			],
			links: buildLocalizedSeoLinks({ matches, internalPath: "/docs" }),
		};
	},
});

const ITEM_META: Record<string, { icon: LucideIcon; description: string }> = {
	submit: {
		icon: Wrench,
		description:
			"List your agent with one POST to /api/agents/submit. A read-only revenue key verifies MRR and publishes the listing instantly. No account required.",
	},
};

function DocsIndexPage() {
	return (
		<div className="space-y-10">
			<JsonLd
				data={breadcrumbListSchema([
					{ name: "Home", path: "/" },
					{ name: "Docs", path: "/docs" },
				])}
			/>
			<div>
				<p className="font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
					&gt; Docs / index
				</p>
				<h1
					className="mt-3 font-mono text-[clamp(1.5rem,3vw,2rem)] leading-[1.1] tracking-[-0.02em] text-foreground"
					style={{ fontWeight: 400 }}
				>
					<span className="text-brand">/</span>docs
				</h1>
				<p className="mt-3 max-w-xl text-base leading-[1.55] text-muted-foreground">
					Everything you need to list your AI agent on AgentMRR and verify
					revenue with a single REST call.
				</p>
			</div>

			{docsSections.map((section) => (
				<div key={section.group}>
					<p className="mb-3 font-mono text-sm uppercase tracking-[0.14em] text-muted-foreground">
						&gt; {section.group}
					</p>
					<div className="grid gap-3 @md/docs:grid-cols-2">
						{section.items.map((item) => {
							const meta = ITEM_META[item.slug];
							const Icon = meta?.icon;
							return (
								<Link
									key={item.slug}
									to={`/docs/${item.slug}`}
									className="group flex flex-col gap-3 border border-border bg-card p-4 transition-colors hover:border-brand/40 hover:bg-brand-soft"
								>
									<div className="flex items-center justify-between">
										{Icon ? (
											<div className="flex size-8 items-center justify-center border border-border bg-background text-muted-foreground transition-colors group-hover:border-brand/40 group-hover:text-brand">
												<Icon className="size-3.5" />
											</div>
										) : null}
										<ArrowUpRight className="size-3.5 text-muted-foreground transition-colors group-hover:text-brand" />
									</div>
									<div>
										<p className="font-mono text-base uppercase tracking-[0.08em] text-foreground">
											{item.label}
										</p>
										{meta?.description ? (
											<p className="mt-1 text-sm leading-normal text-muted-foreground">
												{meta.description}
											</p>
										) : null}
									</div>
								</Link>
							);
						})}
					</div>
				</div>
			))}
		</div>
	);
}
